import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const eventTypeConfig = {
  yoga: { emoji: '🧘', color: '#9C27B0', bg: '#F3E5F5', label: 'Yoga' },
  walk: { emoji: '🚶', color: '#4CAF50', bg: '#E8F5E9', label: 'Walk' },
  health: { emoji: '🏥', color: '#F44336', bg: '#FFEBEE', label: 'Health' },
  cultural: { emoji: '🎵', color: '#FF9800', bg: '#FFF3E0', label: 'Cultural' },
  social: { emoji: '☕', color: '#795548', bg: '#EFEBE9', label: 'Social' },
  general: { emoji: '📌', color: '#2196F3', bg: '#E3F2FD', label: 'General' }
};

const filterTabs = [
  { key: 'all', label: 'All Events', emoji: '📅' },
  { key: 'health', label: 'Health', emoji: '🏥' },
  { key: 'yoga', label: 'Yoga', emoji: '🧘' },
  { key: 'walk', label: 'Walk', emoji: '🚶' },
  { key: 'social', label: 'Social', emoji: '☕' },
  { key: 'cultural', label: 'Cultural', emoji: '🎵' }
];

const formatEventDate = (dateStr, timeStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  let dateLabel;
  if (date.toDateString() === today.toDateString()) {
    dateLabel = 'Today';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    dateLabel = 'Tomorrow';
  } else {
    dateLabel = date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  let timeLabel = '';
  if (timeStr) {
    const [h, m] = timeStr.split(':');
    const t = new Date();
    t.setHours(parseInt(h), parseInt(m));
    timeLabel = t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }

  return { dateLabel, timeLabel };
};

const Events = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/events`, { headers });
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleRegister = async (eventId, isRegistered) => {
    setActionLoading(eventId);
    try {
      if (isRegistered) {
        await axios.delete(`${API_BASE}/api/events/${eventId}/register`, { headers });
        toast.success('Removed from event');
      } else {
        await axios.post(`${API_BASE}/api/events/${eventId}/register`, {}, { headers });
        toast.success('Successfully joined event! 🎉');
      }
      setEvents(events.map(e =>
        e.id === eventId
          ? {
              ...e,
              is_registered: !isRegistered,
              participant_count: isRegistered ? e.participant_count - 1 : e.participant_count + 1
            }
          : e
      ));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update registration');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredEvents = filter === 'all' ? events : events.filter(e => e.event_type === filter);

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #9C27B0 0%, #AB47BC 100%)',
        padding: '16px',
        color: 'white'
      }}>
        <h2 style={{ color: 'white', marginBottom: '4px' }}>📅 Upcoming Events</h2>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
          Activities near you
        </p>
      </div>

      {/* Filter Tabs */}
      <div style={{ padding: '12px 12px 4px', background: 'white', borderBottom: '1px solid #E8E0D8' }}>
        <div className="chips-row">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              className={`chip${filter === tab.key ? ' active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.emoji} {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p style={{ color: '#9C27B0' }}>Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <div className="empty-title">No events found</div>
            <div className="empty-text">Check back soon for upcoming events!</div>
          </div>
        ) : (
          filteredEvents.map((event) => {
            const typeConf = eventTypeConfig[event.event_type] || eventTypeConfig.general;
            const { dateLabel, timeLabel } = formatEventDate(event.event_date, event.event_time);
            const isFull = event.participant_count >= event.max_participants && !event.is_registered;

            return (
              <div key={event.id} className="card" style={{ marginBottom: '12px' }}>
                {/* Event type banner */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: typeConf.bg,
                    padding: '4px 12px',
                    borderRadius: '20px'
                  }}>
                    <span style={{ fontSize: '16px' }}>{typeConf.emoji}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: typeConf.color }}>
                      {typeConf.label}
                    </span>
                  </div>
                  {event.is_registered && (
                    <span style={{
                      background: '#E8F5E9',
                      color: '#1B5E20',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '3px 10px',
                      borderRadius: '12px'
                    }}>
                      ✓ Joined
                    </span>
                  )}
                </div>

                {/* Event title */}
                <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#2D2D2D' }}>
                  {event.title}
                </h3>

                {/* Description */}
                {event.description && (
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px', lineHeight: '1.5' }}>
                    {event.description}
                  </p>
                )}

                {/* Event details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📅</span>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#2D2D2D' }}>
                      {dateLabel}
                      {timeLabel && ` at ${timeLabel}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📍</span>
                    <span style={{ fontSize: '14px', color: '#666' }}>{event.location}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>👥</span>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      {event.participant_count} / {event.max_participants} joined
                    </span>
                    {/* Participant progress bar */}
                    <div style={{ flex: 1, height: '6px', background: '#F0E8E0', borderRadius: '3px' }}>
                      <div style={{
                        width: `${Math.min(100, (event.participant_count / event.max_participants) * 100)}%`,
                        height: '100%',
                        background: isFull ? '#F44336' : typeConf.color,
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                  {event.organizer_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>👤</span>
                      <span style={{ fontSize: '14px', color: '#888' }}>Organized by {event.organizer_name}</span>
                    </div>
                  )}
                </div>

                {/* Join Button */}
                <button
                  className={`btn ${event.is_registered ? 'btn-outline' : isFull ? 'btn-ghost' : 'btn-primary'}`}
                  onClick={() => !isFull && handleRegister(event.id, event.is_registered)}
                  disabled={actionLoading === event.id || isFull}
                  style={{
                    background: event.is_registered ? 'transparent' : isFull ? '#E0E0E0' : undefined,
                    color: event.is_registered ? '#FF6B35' : isFull ? '#999' : undefined,
                    borderColor: event.is_registered ? '#FF6B35' : isFull ? '#E0E0E0' : undefined
                  }}
                >
                  {actionLoading === event.id
                    ? '⏳ Please wait...'
                    : event.is_registered
                      ? '✕ Leave Event'
                      : isFull
                        ? '😔 Event Full'
                        : '✓ Join Event'}
                </button>
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Events;
